/** Aligns with CAM `likelihoodCategories` + 7-bucket `LikelihoodRanges`. */

export type LikelihoodRangeKey =
  | 'Likely'
  | 'Target_Likely'
  | 'Target'
  | 'Target_Reach'
  | 'Reach'
  | 'Reach_FarReach'
  | 'FarReach';

export type LikelihoodRanges = Record<LikelihoodRangeKey, { min: number; max: number }>;

export const EMPTY_LIKELIHOOD_RANGES: LikelihoodRanges = {
  Likely: { min: 0, max: 0 },
  Target_Likely: { min: 0, max: 0 },
  Target: { min: 0, max: 0 },
  Target_Reach: { min: 0, max: 0 },
  Reach: { min: 0, max: 0 },
  Reach_FarReach: { min: 0, max: 0 },
  FarReach: { min: 0, max: 0 },
};

/** GradMap My Take slug → CAM likelihood range key */
export const MY_TAKE_TO_RANGE_KEY: Record<string, LikelihoodRangeKey> = {
  likely: 'Likely',
  'target-likely': 'Target_Likely',
  target: 'Target',
  'target-reach': 'Target_Reach',
  reach: 'Reach',
  'reach-far-reach': 'Reach_FarReach',
  'far-reach': 'FarReach',
};

export const LIKELIHOOD_BALANCE_BUCKETS: {
  rangeKey: LikelihoodRangeKey;
  label: string;
  emoji: string;
  dotBg: string;
}[] = [
  { rangeKey: 'Likely', label: 'Likely', emoji: '👍', dotBg: '#10b981' },
  { rangeKey: 'Target_Likely', label: 'Target-Likely', emoji: '🎯👍', dotBg: '#84cc16' },
  { rangeKey: 'Target', label: 'Target', emoji: '🎯', dotBg: '#eab308' },
  { rangeKey: 'Target_Reach', label: 'Target-Reach', emoji: '🎯🎲', dotBg: '#f59e0b' },
  { rangeKey: 'Reach', label: 'Reach', emoji: '🎲', dotBg: '#f97316' },
  { rangeKey: 'Reach_FarReach', label: 'Reach-Far Reach', emoji: '🎲🚀', dotBg: '#fb7185' },
  { rangeKey: 'FarReach', label: 'Far Reach', emoji: '🚀', dotBg: '#ef4444' },
];

export function normalizeLikelihoodRanges(raw: unknown): LikelihoodRanges {
  const out = { ...EMPTY_LIKELIHOOD_RANGES };
  if (!raw || typeof raw !== 'object') return out;

  for (const key of Object.keys(EMPTY_LIKELIHOOD_RANGES) as LikelihoodRangeKey[]) {
    const entry = (raw as Record<string, { min?: number; max?: number }>)[key];
    if (entry == null) continue;
    const min = Number(entry.min);
    const max = Number(entry.max);
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      out[key] = { min, max };
    }
  }
  return out;
}

export function formatLikelihoodRange(min: number, max: number): string {
  return `${min}-${max}`;
}

/** My Take slug (kebab) → sidebar emoji (CAM-aligned). */
export function likelihoodEmojiForTakeSlug(slug: string | null | undefined): string {
  if (!slug) return '';
  const rangeKey = MY_TAKE_TO_RANGE_KEY[slug];
  if (!rangeKey) return '';
  return LIKELIHOOD_BALANCE_BUCKETS.find((b) => b.rangeKey === rangeKey)?.emoji ?? '';
}
